import React from 'react'
import { Image, Button } from '@deal/components'
import { Link } from 'react-router-dom'
import { getSellableUrl } from '#src/services/sellable'
import { GroupedLineItemsType } from '#src/services/cart'
import defaultProduct from '#src/assets/images/default-product.png'
import ProductLineItemPrice from '../ProductLineItemPrice'
import CartAlerts from '../CartAlerts'
import styles from './styles.css'
import { Experiment, Variation } from '@deal/experiment-js'
import CloseIcon from './close.svg'
import { TrackableSellableFragment, OrderLineItemFragment } from '#src/generated/graphql'

interface Props {
  group: GroupedLineItemsType
  onRemoveItem: (lineItemId: string, sellable?: TrackableSellableFragment) => void
}

class ProductLineItemGroup extends React.Component<Props> {
  public isUrgent(lineItem: OrderLineItemFragment) {
    return (
      lineItem.source.__typename === 'OrderLineItemSellableSource' &&
      lineItem.source.sellable.inShortSupply
    )
  }
  public getLineItemImage(lineItem: OrderLineItemFragment) {
    // Going to leave this if check for now since this will probably behave a little
    // different with adHoc and expert item sources
    if (lineItem.source.__typename !== 'OrderLineItemSellableSource') {
      return defaultProduct
    }

    if (lineItem.source.sellable && lineItem.source.sellable.images.length > 0) {
      return lineItem.source.sellable.images[0].url
    } else {
      return defaultProduct
    }
  }

  public getLineItemSellable(lineItem: OrderLineItemFragment) {
    if (lineItem.source.__typename === 'OrderLineItemSellableSource') {
      return lineItem.source.sellable
    }

    return undefined
  }

  public getLineItemSellableVariants(lineItem: OrderLineItemFragment) {
    const sellable = this.getLineItemSellable(lineItem)
    if (!sellable) {
      return null
    }

    return sellable.variants
  }

  public render() {
    const { group } = this.props
    const { parentLineItem } = group

    const lineItemImage = this.getLineItemImage(parentLineItem)
    const sellable = this.getLineItemSellable(group.parentLineItem)
    const sellableVariants = this.getLineItemSellableVariants(group.parentLineItem)
    const originalPrice = sellable && sellable.originalPrice ? sellable.originalPrice.amount : null

    return (
      <>
        <div className={styles.lineItem}>
          <div className={styles.imageContainer}>
            {sellable && (
              <Link to={getSellableUrl(sellable)} className={styles.link}>
                <Image src={lineItemImage} width={100} imgixParams={{ fit: 'clip' }} />
              </Link>
            )}
            {!sellable && <Image src={lineItemImage} width={100} imgixParams={{ fit: 'clip' }} />}
          </div>
          <div className={styles.details}>
            {sellable && (
              <Link to={getSellableUrl(sellable)} className={styles.title}>
                {parentLineItem.title}
              </Link>
            )}
            {!sellable && <span className={styles.title}>{parentLineItem.title}</span>}

            {sellableVariants && (
              <div className={styles.variants}>
                {sellableVariants.map((variant, idx) => (
                  <span key={variant.displayValueHtml}>
                    {variant.displayValueHtml}
                    {idx === sellableVariants.length - 1 ? '' : ', '}
                  </span>
                ))}
              </div>
            )}

            <div className={styles.quantity}>Quantity: {parentLineItem.quantity}</div>

            {/* If there are no children we just render the price for parent sellable, otherwise we map over all grouped children line items*/}
            {group.children.length === 0 ? (
              <ProductLineItemPrice lineItem={group.parentLineItem} originalPrice={originalPrice} />
            ) : (
              group.children.map(child => (
                <ProductLineItemPrice
                  key={child.id}
                  lineItem={child}
                  originalPrice={originalPrice}
                />
              ))
            )}

            <Experiment id="shopping-cart-urgency-label" default="off">
              <Variation id="off"  />
              <Variation id="limited-availability" >
                {this.isUrgent(parentLineItem) && (
                  <div className={styles.calloutLabel}>🔥 Limited Availability</div>
                )}
              </Variation>
              <Variation id="high-sellout-risk">
                {this.isUrgent(parentLineItem) && (
                  <div className={styles.calloutLabel}>🔥 High sellout risk</div>
                )}
              </Variation>
            </Experiment>
          </div>
          <div className={styles.removeBox}>
            <Button
              variant="link"
              className={styles.removeButton}
              size="large"
              onClick={() => {
                this.props.onRemoveItem(parentLineItem.id, this.getLineItemSellable(parentLineItem))
              }}
            >
              <CloseIcon className={styles.removeIcon} fill="#c3c7ca" />
            </Button>
          </div>
        </div>
        <CartAlerts messages={parentLineItem.messages} />
      </>
    )
  }
}

export default ProductLineItemGroup
